import { useRef } from 'react';
import { useLocation } from '@/contexts/LocationContext';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import { PurchaseOrder } from '../types';
import { generateFinanceCSV } from '../utils/purchaseOrderUtils';
import type { CompletePurchaseOrderInput, DeleteDraftInput, UpdateOrderStatusInput } from '@threadheads/shared';
import { useTranslation } from 'react-i18next';
// Using shared schema for type safety

/**
 * BLU-530: a status transition that lost the backend idempotency CAS to a
 * concurrent/divergent transition returns a tRPC CONFLICT (HTTP 409). That is
 * not a real failure — the order simply moved underneath this request — so it
 * should refresh the list and inform rather than show a destructive error.
 */
const isConflictError = (error: unknown): boolean => trpcErrorCode(error) === 'CONFLICT';

/**
 * Hook for general purchase order mutations
 * Handles status updates, deletions, and CSV downloads
 */
export function useOrderMutations() {
  const { t } = useTranslation('orders');
  const { selectedLocation } = useLocation();
  const utils = trpc.useUtils();
  /**
   * Set for the duration of one `updateOrderStatus` call whose caller has said
   * it will report the failure itself.
   *
   * The receiving screen flips the status after a batch of receives that have
   * already succeeded. When the backend refuses that flip, a generic red
   * "Error updating status" banner reads as the receives having failed, which
   * is the opposite of what happened. The caller owns the wording in that case;
   * the Sentry capture still happens here, and the conflict path is untouched.
   */
  const suppressStatusErrorToast = useRef(false);
  const resolveLocationId = (locationId?: string | null) => {
    const resolved = locationId ?? selectedLocation?.id ?? null;
    if (!resolved) {
      throw new Error("No location selected");
    }
    return resolved;
  };

  // UPDATE ORDER STATUS
  const updateStatusMutation = trpc.purchaseOrders.updateOrderStatus.useMutation({
    onSuccess: (_result, variables: UpdateOrderStatusInput) => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();

      toast({
        title: t('toast.statusUpdateTitle'),
        description: t('toast.statusUpdateDescription', { status: variables.status.replace('_', ' ') }),
      });
    },
    onError: (error) => {
      if (isConflictError(error)) {
        utils.purchaseOrders.getOrders.invalidate();
        utils.purchaseOrders.getOrder.invalidate();
        toast({
          title: t('toast.statusUpdateTitle'),
          description: 'This order was just updated elsewhere, refreshed to the latest status.',
        });
        return;
      }

      Sentry.captureException(error, {
        tags: {
          component: 'useOrderMutations',
          operation: 'updateStatus',
          location_id: selectedLocation?.id
        }
      });

      if (suppressStatusErrorToast.current) return;

      toast({
        title: t('toast.errorUpdatingStatus'),
        description: error.message || t('toast.unknownError'),
        variant: "destructive",
      });
    }
  });

  // CANCEL ORDER (soft cancel - sets status to 'cancelled')
  const deleteOrderMutation = trpc.purchaseOrders.updateOrderStatus.useMutation({
    onSuccess: (_result, _variables: UpdateOrderStatusInput) => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();

      toast({
        title: t('toast.orderCancelled'),
        description: t('toast.orderCancelledDescription'),
      });
    },
    onError: (error) => {
      if (isConflictError(error)) {
        utils.purchaseOrders.getOrders.invalidate();
        utils.purchaseOrders.getOrder.invalidate();
        toast({
          title: t('toast.orderCancelled'),
          description: 'This order was just updated elsewhere, refreshed to the latest status.',
        });
        return;
      }

      Sentry.captureException(error, {
        tags: {
          component: 'useOrderMutations',
          operation: 'deleteOrder',
          location_id: selectedLocation?.id
        }
      });

      toast({
        title: t('toast.errorCancellingOrder'),
        description: error.message || t('toast.unknownError'),
        variant: "destructive",
      });
    }
  });

  // MARK ORDER PAID (BLU-373 PR2 — Admin only, locks exchange_rate)
  const markPaidMutation = trpc.purchaseOrders.markOrderPaid.useMutation({
    onSuccess: () => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();

      toast({
        title: 'Order marked as paid',
        description: 'Exchange rate is now locked for this order.',
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'useOrderMutations',
          operation: 'markOrderPaid',
          location_id: selectedLocation?.id,
        },
      });

      toast({
        title: 'Failed to mark order as paid',
        description: error.message || t('toast.unknownError'),
        variant: 'destructive',
      });
    },
  });

  // UNDO ORDER STATUS
  const undoStatusMutation = trpc.purchaseOrders.undoOrderStatus.useMutation({
    onSuccess: () => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();

      toast({
        title: t('toast.statusUpdateTitle'),
        description: 'Status reverted to previous stage.',
      });
    },
    onError: (error) => {
      if (isConflictError(error)) {
        utils.purchaseOrders.getOrders.invalidate();
        utils.purchaseOrders.getOrder.invalidate();
        toast({
          title: t('toast.statusUpdateTitle'),
          description: 'This order was just updated elsewhere, refreshed to the latest status.',
        });
        return;
      }

      Sentry.captureException(error, {
        tags: {
          component: 'useOrderMutations',
          operation: 'undoStatus',
          location_id: selectedLocation?.id
        }
      });

      toast({
        title: 'Failed to undo status',
        description: error.message || t('toast.unknownError'),
        variant: "destructive",
      });
    }
  });

  // COMPLETE PO (BLU-572 — short-close a partially received PO: flips
  // partially_received → received so the leftover ordered−received stops
  // counting as incoming. No stock movement; received quantities preserved.)
  const completePurchaseOrderMutation = trpc.purchaseOrders.completePurchaseOrder.useMutation({
    onSuccess: (result) => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();

      toast({
        title: t('toast.statusUpdateTitle'),
        description: result && 'alreadyApplied' in result && result.alreadyApplied
          ? 'This order was already completed elsewhere, refreshed to the latest status.'
          : 'Purchase order completed. The remaining ordered quantity no longer counts as incoming.',
      });
    },
    onError: (error) => {
      if (isConflictError(error)) {
        utils.purchaseOrders.getOrders.invalidate();
        utils.purchaseOrders.getOrder.invalidate();
        toast({
          title: t('toast.statusUpdateTitle'),
          description: 'This order was just updated elsewhere, refreshed to the latest status.',
        });
        return;
      }

      Sentry.captureException(error, {
        tags: {
          component: 'useOrderMutations',
          operation: 'completePurchaseOrder',
          location_id: selectedLocation?.id
        }
      });

      toast({
        title: t('toast.errorUpdatingStatus'),
        description: error.message || t('toast.unknownError'),
        variant: "destructive",
      });
    }
  });

  const deleteDraftMutation = trpc.purchaseOrders.deleteDraft.useMutation({
    onSuccess: () => {
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrder.invalidate();

      toast({
        title: t('toast.orderDeleted'),
        description: t('toast.orderDeletedDescription'),
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'useOrderMutations',
          operation: 'deleteDraft',
          location_id: selectedLocation?.id
        },
        level: 'warning'
      });

      toast({
        title: t('toast.errorDeletingOrder'),
        description: error.message || t('toast.unknownError'),
        variant: "destructive",
      });
    }
  });

  // DOWNLOAD FINANCE CSV (utility function - not a mutation)
  const downloadFinanceCSV = (order: PurchaseOrder) => {
    const csvContent = generateFinanceCSV(order, order.additional_costs);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${order.po_number}-finance-report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    toast({
      title: t('toast.csvDownloaded'),
      description: t('toast.csvDownloadedDescription', { poNumber: order.po_number }),
    });
  };

  return {
    updateOrderStatus: async (
      orderId: string,
      status: UpdateOrderStatusInput['status'],
      locationId?: string | null,
      options?: { suppressErrorToast?: boolean }
    ) => {
      const resolvedLocationId = resolveLocationId(locationId);

      suppressStatusErrorToast.current = options?.suppressErrorToast === true;
      try {
        await updateStatusMutation.mutateAsync({
          orderId,
          locationId: resolvedLocationId,
          status
        });
      } finally {
        suppressStatusErrorToast.current = false;
      }
    },
    deleteOrder: async (orderId: string, locationId?: string | null) => {
      const resolvedLocationId = resolveLocationId(locationId);

      await deleteOrderMutation.mutateAsync({
        orderId,
        locationId: resolvedLocationId,
        status: 'cancelled'
      });
    },
    deleteDraftOrder: async (orderId: string, locationId?: string | null) => {
      const resolvedLocationId = resolveLocationId(locationId);

      const payload: DeleteDraftInput = {
        orderId,
        locationId: resolvedLocationId,
      };

      await deleteDraftMutation.mutateAsync(payload);
    },
    undoOrderStatus: async (orderId: string, locationId?: string | null) => {
      const resolvedLocationId = resolveLocationId(locationId);

      await undoStatusMutation.mutateAsync({
        orderId,
        locationId: resolvedLocationId,
      });
    },
    markOrderPaid: async (orderId: string, locationId?: string | null) => {
      const resolvedLocationId = resolveLocationId(locationId);

      await markPaidMutation.mutateAsync({
        orderId,
        locationId: resolvedLocationId,
      });
    },
    completePurchaseOrder: async (orderId: string, locationId?: string | null) => {
      const resolvedLocationId = resolveLocationId(locationId);

      const payload: CompletePurchaseOrderInput = {
        orderId,
        locationId: resolvedLocationId,
      };

      await completePurchaseOrderMutation.mutateAsync(payload);
    },
    downloadFinanceCSV,
    isUpdating: updateStatusMutation.isPending,
    isUndoing: undoStatusMutation.isPending,
    isMarkingPaid: markPaidMutation.isPending,
    isCompleting: completePurchaseOrderMutation.isPending,
    isDeleting: deleteOrderMutation.isPending,
    isDeletingDraft: deleteDraftMutation.isPending,
  };
}
